import React, { useEffect, useState, forwardRef,
  useImperativeHandle } from "react";
import { useTranslation } from 'react-i18next';
import {
  View,
  Text,
  StyleSheet,
  Platform,
} from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  testID?: string | undefined;
};

export interface MapiSenderDateOfBirthStepHandle {
  submit: () => Promise<boolean>;
}

function parseMMDDYYYY(text: string): Date {
  const [mm, dd, yyyy] = text.split("/").map((p) => parseInt(p, 10));
  return new Date(yyyy, mm - 1, dd, 12, 0, 0, 0);
}

const MapiSenderDateOfBirthStep = forwardRef<MapiSenderDateOfBirthStepHandle, Props>(({ onValidationChange, testID }: Props, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({ dob: "" });

  const storedDobString = useSessionStore((s: SessionState) => (s as any).senderDOBString) || "";
  const [dob, setDob] = useState(storedDobString);
  const setSenderDOBString = useSessionStore((s: SessionState) => (s as any).setSenderDOBString);
  const setSenderDOB = useSessionStore((s: SessionState) => (s as any).setSenderDOB);

  const isValid = dob.trim().length > 8;

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  useEffect(() => {
    setDob(storedDobString || "");
  }, [storedDobString]);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      const newErrors = {
        dob: dob.trim() ? "" : "Date of birth is required",
      };
      setErrors(newErrors);
      return !Object.values(newErrors).some(Boolean);
    },
  }));

  const formatDobInput = (raw: string): string => {
    const digits = (raw || "").replace(/\D/g, "").slice(0, 8);
    const mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    if (digits.length <= 2) return mm;
    if (digits.length <= 4) return `${mm}/${dd}`;
    return `${mm}/${dd}/${yyyy}`;
  };

  const handleManualInput = (rawText: string) => {
    const formatted = formatDobInput(rawText);
    setDob(formatted);
    try { setSenderDOBString?.(formatted); } catch {}
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(19|20)\d{2}$/;
    if (regex.test(formatted)) {
      const parsed = parseMMDDYYYY(formatted);
      try { setSenderDOB?.(parsed); } catch {}
      if (errors.dob) setErrors({ dob: "" });
    }
  };

  const isStartTyping = dob.trim().length > 0;

  return (
    <View style={styles.container} testID={testID}>
      <Text style={styles.title}>{t("SENDER_DOB_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("SENDER_DOB_SUBTITLE")}</Text>

      <FloatingTextInputComponent
        testID="mapi-senderDOB"
        isAutoFocus={true}
        label={isStartTyping ? "Date" : "MM/DD/YYYY"}
        value={dob}
        onChangeText={handleManualInput}
        errorText={errors.dob}
        keyboardType={"number-pad"}
      />
      <View style={styles.infoBox}>
        <Text style={styles.infoText}>
          {t("YOUR_INFO_IS_ALWAYS_KEPT_PRIVATE")}
        </Text>
      </View>
    </View>
  );
});

export default MapiSenderDateOfBirthStep;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500',
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  },
  infoBox: {
    marginTop: 20,
    padding: 12,
    borderWidth: 1,
    borderColor: Colors.titleTextColor,
    borderRadius: 8,
  },
  infoText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    color: Colors.darkGrey,
  },
});
